/** @jsx jsx */
/** @jsxRuntime classic */
import { Keyframes, jsx, keyframes } from '@emotion/react';
import React, { forwardRef } from 'react';
import Box from '../Box/Box';
import useTheme from '../style/useTheme';

export type Props = {
  className?: string;
  ['data-testid']?: string;
};

const Ellipse: React.FC<{
  delay?: number;
  animation?: Keyframes;
  baseColor: string;
}> = ({ delay = 0, animation, baseColor }) => (
  <Box
    sx={{
      display: 'flex',
      width: '12px',
      height: '12px',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: baseColor,
        animation: `${animation} 2s infinite linear`,
        animationDelay: `${delay}ms`,
      }}
    />
  </Box>
);

const BlueDotLoader = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { resolveColor, theme } = useTheme();
  const color = resolveColor({
    type: 'theme',
    colorLegacy: theme.components.dotLoader.default.color.legacy,
    colorNext: theme.components.dotLoader.default.color.next,
  });
  const lightColor = resolveColor({
    type: 'theme',
    colorLegacy: theme.components.dotLoader.default.lightColor.legacy,
    colorNext: theme.components.dotLoader.default.lightColor.next,
  });

  const dotFlashingTempo1 = keyframes`
    0%, 100% {
      background-color: ${color};
      width: 12px;
      height: 12px;
    }
    12.5% {
      background-color: ${lightColor};
      width: 6px;
      height: 6px;
    }
    87.5% {
      background-color: ${lightColor};
      width: 6px;
      height: 6px;
    }
  `;
  const dotFlashingTempo2 = keyframes`
    0%, 100% {
      background-color:${color};
      width: 12px;
      height: 12px;
    }
    12.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
    25% {
      background-color:${color};
      width: 12px;
      height: 12px;
    }
    37.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
    87.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
  `;
  const dotFlashingTempo3 = keyframes`
    0%, 100% {
      background-color:${color};
      width: 12px;
      height: 12px;
    }
    12.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
    37.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
    50% {
      background-color:${color};
      width: 12px;
      height: 12px;
    }
    62.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
    87.5% {
      background-color:${lightColor};
      width: 6px;
      height: 6px;
    }
  `;
  return (
    <Box
      ref={ref}
      data-testid={props['data-testid'] || 'DotLoader'}
      className="origami-DotLoader"
      sx={{
        display: 'flex',
      }}
    >
      <Ellipse delay={0} animation={dotFlashingTempo1} baseColor={lightColor} />
      <Ellipse
        delay={1750}
        animation={dotFlashingTempo2}
        baseColor={lightColor}
      />
      <Ellipse
        delay={1500}
        animation={dotFlashingTempo3}
        baseColor={lightColor}
      />
      <Ellipse
        delay={750}
        animation={dotFlashingTempo2}
        baseColor={lightColor}
      />
      <Ellipse
        delay={1000}
        animation={dotFlashingTempo1}
        baseColor={lightColor}
      />
    </Box>
  );
});

export default BlueDotLoader;
